import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Skeleton, Spin } from 'antd';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { MODAL_STYLES } from '../../../styles/modalStyles';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import {
  projectService,
  ProjectExecution,
} from '../Project/ProjectService';
import { k1Service, PartnerK1Summary } from '../K1Service';
import { ENV } from '../../../config';
import styles from './GenerateTaxForm.module.css';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
LicenseManager.setLicenseKey(ENV.AG_GRID_API_KEY || '');

const taxFormGridTheme = themeQuartz.withParams({
  backgroundColor: 'transparent',
  foregroundColor: 'rgba(255,255,255,0.85)',
  borderColor: 'rgba(255,255,255,0.08)',
  oddRowBackgroundColor: 'rgba(255,255,255,0.015)',
  headerBackgroundColor: 'rgba(255,255,255,0.03)',
  headerTextColor: 'rgba(255,255,255,0.4)',
  rowHoverColor: 'rgba(255,255,255,0.025)',
  accentColor: '#F26522',
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
});

const taxFormStatusBar = {
  statusPanels: [
    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
    { statusPanel: 'agTotalRowCountComponent', align: 'center' as const },
    { statusPanel: 'agAggregationComponent' },
  ],
};

export const GenerateTaxForm: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();

  const [project, setProject] = useState<ProjectExecution | null>(null);
  const [partners, setPartners] = useState<PartnerK1Summary[]>([]);
  const [loading, setLoading] = useState(false);
  const [noProject, setNoProject] = useState(false);
  const [goSystemLoading, setGoSystemLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewPartner, setPreviewPartner] = useState<PartnerK1Summary | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchData = useCallback(async (partnershipId: string, year: string) => {
    setLoading(true);
    setProject(null);
    setPartners([]);
    setNoProject(false);
    try {
      const proj = await projectService.getByPartnership(partnershipId, year);
      setProject(proj);
      const partnerList = await k1Service.getPartnerList(proj.id);
      setPartners(partnerList);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) {
        setNoProject(true);
      } else {
        console.error('[GenerateTaxForm] fetchData error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership) {
      setNoProject(false);
      fetchData(selectedPartnership.id, taxYear);
    } else {
      setProject(null);
      setPartners([]);
      setNoProject(false);
    }
  }, [selectedPartnership, taxYear, fetchData]);

  const totalPartners = partners.length;
  const k1sReady = partners.filter(p => p.status === 'Ready').length;
  const totalIncome = partners.reduce((sum, p) => sum + p.incomeAllocated, 0);
  const displayIncome = totalPartners > 0
    ? `$${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '$0';

  const handleGenerateAll = () => {
    message.success(`${totalPartners} K-1s generated — ready for GoSystem export`);
  };

  const handleExportGoSystemXml = async () => {
    if (!project) return;
    setGoSystemLoading(true);
    try {
      const blob = await projectService.exportGoSystemXml(project.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GoSystem_PartnerPackage_${project.id}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed — please try again');
    } finally {
      setGoSystemLoading(false);
    }
  };

  const handlePreviewK1 = useCallback(async (partner: PartnerK1Summary) => {
    if (!project) return;
    setPreviewPartner(partner);
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const blob = await projectService.downloadK1Pdf(project.id, partner.entityId);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch {
      message.error('Failed to load K-1 preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [project]);

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewPartner(null);
  };

  const handleDownloadFromPreview = () => {
    if (!previewPartner || !previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `K1_${previewPartner.partnerName.replace(/ /g, '_')}_2024.pdf`;
    a.click();
  };

  const handleExportGoSystemSingle = async () => {
    if (!project) return;
    setGoSystemLoading(true);
    try {
      const blob = await projectService.exportGoSystemXml(project.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GoSystem_PartnerPackage_${project.id}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed — please try again');
    } finally {
      setGoSystemLoading(false);
    }
  };

  const colDefs = useMemo<ColDef<PartnerK1Summary>[]>(() => [
    {
      field: 'partnerName',
      headerName: 'PARTNER',
      pinned: 'left' as const,
      flex: 2,
      minWidth: 200,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: false,
      cellRenderer: (params: ICellRendererParams<PartnerK1Summary>) => (
        <div>
          <div style={{ fontWeight: 500, color: '#ffffff', fontSize: 13 }}>{params.value as string}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
            ID-{params.data?.entityId}
          </div>
        </div>
      ),
    },
    {
      headerName: 'ENTITY TYPE',
      valueGetter: () => 'LP',
      width: 130,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: false,
    },
    {
      field: 'ownershipPct',
      headerName: 'OWNERSHIP %',
      aggFunc: 'sum',
      width: 160,
      enableRowGroup: false,
      enablePivot: false,
      enableValue: true,
      valueFormatter: (p) => p.value != null ? `${(p.value as number).toFixed(4)}%` : '',
    },
    {
      field: 'incomeAllocated',
      headerName: 'INCOME ALLOCATED',
      aggFunc: 'sum',
      flex: 1,
      minWidth: 180,
      enableRowGroup: false,
      enablePivot: false,
      enableValue: true,
      valueFormatter: (p) => p.value != null
        ? `$${(p.value as number).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        : '',
    },
    {
      field: 'status',
      headerName: 'STATUS',
      width: 180,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: false,
      cellRenderer: (_params: ICellRendererParams<PartnerK1Summary>) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '3px 9px',
          borderRadius: 4, fontSize: 11.5, fontWeight: 500,
          background: 'rgba(37,99,235,0.15)', color: '#60a5fa',
          border: '1px solid rgba(37,99,235,0.25)',
        }}>
          Ready to generate
        </span>
      ),
    },
    {
      headerName: 'ACTIONS',
      width: 140,
      sortable: false,
      filter: false,
      enableRowGroup: false,
      enablePivot: false,
      enableValue: false,
      cellRenderer: (params: ICellRendererParams<PartnerK1Summary>) => (
        <button
          style={{
            height: 28, padding: '0 12px', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.18)', borderRadius: 5,
            color: 'rgba(255,255,255,0.75)', fontSize: 12, cursor: 'pointer',
            fontFamily: 'Inter, sans-serif', fontWeight: 500,
          }}
          onClick={() => { if (params.data) handlePreviewK1(params.data); }}
        >
          Preview K-1
        </button>
      ),
    },
  ], [handlePreviewK1]);

  const partnerGridDefaultColDef = useMemo<ColDef<PartnerK1Summary>>(() => ({
    resizable: true,
    sortable: true,
    filter: true,
  }), []);

  if (!selectedPartnership) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FileTextOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyText}>Select a client and partnership to generate K-1s.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.loadingWrap}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  if (noProject) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📁</div>
          <div className={styles.emptyText}>
            No project found for {selectedPartnership?.name} · {taxYear}. Create a project first.
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>⚙️</div>
          <div className={styles.emptyText}>
            No calculation results found. Run calculations from the Project screen first.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <h1 className={styles.pageTitle}>Generate Tax Form</h1>
        <p className={styles.pageSub}>
          Schedule K-1 · {selectedPartnership.name} · {taxYear}
        </p>
      </div>

      {/* Summary card */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryLeft}>
          <div className={styles.summaryForm}>Schedule K-1 (Form 1065)</div>
          <div className={styles.summaryTaxYear}>{taxYear.replace('FY', '')}</div>
        </div>
        <div className={styles.statsRow}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{totalPartners}</div>
            <div className={styles.statLabel}>PARTNERS</div>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <div className={styles.statValue}>{k1sReady || totalPartners}</div>
            <div className={styles.statLabel}>K-1S READY</div>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <div className={`${styles.statValue} ${styles.statValueOrange}`}>{displayIncome}</div>
            <div className={styles.statLabel}>TOTAL INCOME</div>
          </div>
        </div>
      </div>

      {/* Partner grid */}
      <div className={styles.tableSection}>
        <div className={styles.tableTopBar}>
          <div className={styles.tableCount}>
            {totalPartners} partner{totalPartners !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={styles.btnGenerate}
              onClick={handleExportGoSystemXml}
              disabled={goSystemLoading}
            >
              <DownloadOutlined style={{ marginRight: 6 }} />
              {goSystemLoading ? 'Preparing export...' : 'Export GoSystem XML'}
            </button>
            <button className={styles.btnGenerate} onClick={handleGenerateAll}>
              Generate all K-1s
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 400 }}>
          <AgGridReact
            rowData={partners}
            columnDefs={colDefs}
            defaultColDef={partnerGridDefaultColDef}
            theme={taxFormGridTheme}
            domLayout="autoHeight"
            sideBar={true}
            cellSelection={true}
            enableCellTextSelection={true}
            rowGroupPanelShow="always"
            groupDisplayType="multipleColumns"
            suppressMenuHide={false}
            statusBar={taxFormStatusBar}
            rowHeight={48}
            headerHeight={36}
          />
        </div>
      </div>

      {/* K-1 Preview Modal */}
      <Modal
        open={previewOpen}
        onCancel={handleClosePreview}
        width="80%"
        style={{ top: 20 }}
        title={`K-1 Preview — ${previewPartner?.partnerName ?? ''}`}
        styles={{
          ...MODAL_STYLES,
          body: { padding: 0 },
        }}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={handleDownloadFromPreview}
            disabled={previewLoading || !previewUrl}
          >
            <DownloadOutlined />
            Download PDF
          </Button>,
          <Button
            key="gosystem"
            onClick={handleExportGoSystemSingle}
            disabled={previewLoading || goSystemLoading}
          >
            Export GoSystem XML
          </Button>,
          <Button key="close" onClick={handleClosePreview}>
            Close
          </Button>,
        ]}
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
              Loading K-1...
            </div>
          </div>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            width="100%"
            height="700px"
            style={{ border: 'none', display: 'block' }}
            title="K-1 Preview"
          />
        ) : null}
      </Modal>
    </div>
  );
};
